import { UcoastEl } from '@/scripts/core/UcoastEl'
import { formatPhoneNumber, isValidPhoneNumber } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type WelcomePopup } from '@/scripts/theme/welcome-popup'

export class KlaviyoForm extends UcoastEl {
	static htmlSelector = 'klaviyo-form'
	form: HTMLFormElement
	submitButton: HTMLButtonElement
	emailInput: HTMLInputElement
	phoneInput?: HTMLInputElement
	formContentEl?: HTMLElement
	successMessageEl?: HTMLElement
	errorMessageEl?: HTMLElement
	errorMessage: string
	collapseOnSuccess: boolean
	route: string
	popup?: WelcomePopup
	termsElements?: {
		termsAccepted: HTMLButtonElement
		termsLabel: HTMLLabelElement
	}
	termsAccepted = true
	companyId: string

	constructor() {
		super()
		this.form = q.rs('form', this)
		this.submitButton = q.rs('[type="submit"]', this)
		this.emailInput = q.rs('[type="email"]', this)
		this.phoneInput = q.os<HTMLInputElement>(
			'input[name="phone_number"]',
			this
		)
		this.formContentEl = q.os<HTMLElement>('[data-form-content]', this)
		this.successMessageEl = q.os<HTMLElement>('[data-form-status]', this)
		this.errorMessageEl = q.os<HTMLElement>('[data-form-error]', this)
		this.errorMessage =
			this.form.getAttribute('data-error') ?? 'Please try again.'
		this.collapseOnSuccess =
			this.form.getAttribute('data-collapse-on-success') === 'true'
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
		this.emailInput.addEventListener('input', () => this.clearError())
		this.phoneInput?.addEventListener('input', () => this.clearError())
		this.phoneInput?.addEventListener('blur', () =>
			this.normalizePhoneInput()
		)
		this.popup = q.os<WelcomePopup>('welcome-popup')
		this.termsElements = this.getTermsElements()
		this.termsSetup()
		this.companyId = q.ra(this, 'data-company-id')
		this.route = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`
	}

	getTermsElements() {
		const termsAccepted = q.os<HTMLButtonElement>(
			'button[data-terms-accepted]',
			this
		)
		const termsLabel = q.os<HTMLLabelElement>('[data-terms-label]', this)
		if (termsAccepted && termsLabel) {
			return {
				termsAccepted,
				termsLabel,
			}
		}
		return undefined
	}

	termsSetup() {
		if (!this.termsElements) return
		this.termsAccepted = false
		this.termsElements.termsAccepted.addEventListener(
			'click',
			this.toggleTerms.bind(this)
		)
		this.termsElements.termsLabel.addEventListener('click', (event) => {
			const target = event.target as HTMLElement | null
			if (target && target.closest('a')) return
			this.toggleTerms()
		})
	}

	toggleTerms() {
		if (!this.termsElements) throw new Error('Terms elements not found')
		if (this.termsAccepted) {
			this.termsAccepted = false
			this.termsElements.termsAccepted.classList.remove('active')
			this.submitButton.setAttribute('disabled', '')
		} else {
			this.termsElements.termsAccepted.classList.add('active')
			this.submitButton.removeAttribute('disabled')
			this.termsAccepted = true
		}
	}

	clearError() {
		this.emailInput.removeAttribute('aria-invalid')
		this.phoneInput?.removeAttribute('aria-invalid')
		this.errorMessageEl?.classList.add('hidden')
	}

	showError(message: string, input?: HTMLInputElement) {
		if (this.errorMessageEl) {
			this.errorMessageEl.textContent = message
			this.errorMessageEl.classList.remove('hidden')
			this.errorMessageEl.focus()
		}
		input?.setAttribute('aria-invalid', 'true')
	}

	normalizePhoneInput() {
		if (
			!this.phoneInput?.value ||
			!isValidPhoneNumber(this.phoneInput.value)
		)
			return
		this.phoneInput.value = formatPhoneNumber(this.phoneInput.value)
	}

	validate() {
		const email = this.emailInput.value.trim()
		this.emailInput.value = email
		if (!email) {
			this.showError('Please enter your email address.', this.emailInput)
			return false
		}
		if (!this.emailInput.checkValidity()) {
			this.showError(
				'Please enter a valid email address.',
				this.emailInput
			)
			return false
		}
		if (
			this.phoneInput?.value &&
			!isValidPhoneNumber(this.phoneInput.value)
		) {
			this.showError(
				'Please enter a valid phone number. Include + and the country code for numbers outside the US or Canada.',
				this.phoneInput
			)
			return false
		}
		this.normalizePhoneInput()
		return true
	}

	setSubmitting(isSubmitting: boolean) {
		this.submitButton.classList.toggle('loading', isSubmitting)
		if (isSubmitting) {
			this.form.setAttribute('aria-busy', 'true')
			this.submitButton.setAttribute('aria-disabled', 'true')
			this.submitButton.disabled = true
			return
		}
		this.form.removeAttribute('aria-busy')
		this.submitButton.removeAttribute('aria-disabled')
		this.submitButton.disabled = Boolean(
			this.termsElements && !this.termsAccepted
		)
	}

	showSuccess() {
		this.form.reset()
		this.clearError()
		this.form.classList.add('is-success')
		if (this.collapseOnSuccess) {
			this.formContentEl?.setAttribute('hidden', '')
		}
		this.successMessageEl?.classList.remove('hidden')
		this.successMessageEl?.focus()
		if (this.popup) {
			window.setTimeout(() => {
				this.popup?.hide()
			}, 1000)
		}
	}

	async onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		this.clearError()
		if (!this.validate()) return

		this.setSubmitting(true)
		this.successMessageEl?.classList.add('hidden')

		const formData = new FormData(this.form)
		const config = this.addToKlaviyoListConfig(formData)

		try {
			const response = await fetch(this.route, config)
			if (!response.ok) {
				this.showError(this.errorMessage)
				return
			}
			this.showSuccess()
		} catch {
			this.showError(this.errorMessage)
		} finally {
			this.setSubmitting(false)
		}
	}

	addToKlaviyoListConfig(body: FormData) {
		const values = {
			email: q.rfd(body, 'email'),
			list_id: q.rfd(body, 'list_id'),
			custom_source: q.rfd(body, 'custom_source'),
			phone_number: q.ofd(body, 'phone_number'),
			first_name: q.ofd(body, 'first_name'),
			language_iso: q.ofd(body, 'language_iso') ?? 'en',
			market_handle: q.ofd(body, 'market_handle') ?? 'us',
		}
		if (values.phone_number) {
			values.phone_number = formatPhoneNumber(values.phone_number)
		}
		const profileAttributes: Record<string, unknown> = {
			email: values.email,
			properties: {
				language_iso: values.language_iso,
				market_handle: values.market_handle,
			},
		}
		if (values.phone_number)
			profileAttributes.phone_number = values.phone_number
		if (values.first_name) profileAttributes.first_name = values.first_name
		const data = {
			data: {
				type: 'subscription',
				attributes: {
					custom_source: values.custom_source,
					profile: {
						data: {
							type: 'profile',
							attributes: profileAttributes,
						},
					},
				},
				relationships: {
					list: {
						data: {
							type: 'list',
							id: values.list_id,
						},
					},
				},
			},
		}
		return {
			method: 'POST',
			mode: 'cors' as RequestMode,
			headers: {
				'Content-Type': 'application/json',
				Revision: '2026-07-15',
			},
			body: JSON.stringify(data),
		}
	}
}
